import React from "react"
import GridSearchTemplate from "../GridSearchTemplate"
import { ExtenalEntity, Hotels } from "../../api/services/next"
import { useTextResources } from "../../language"
import { Form } from "semantic-ui-react"
import CheckBox from "../../components/shared/Checkbox";
import EntityTypesDropDown from "../../components/shared/EntityTypesDropDown"
import { useState, useCallback } from "react"
import { onUpdateGridHandler } from "../../components/GridPanel/utility"
import { useEffect } from "react"
import { EntityType, EntityTypes, MenuActions } from "../../components/shared/constants"
import TextInput from "../../components/shared/TextInput"
import { onError, onSuccess } from "../../components/shared/messages"
import { endpoint } from "../../api/http"
import HotelsDropDown from "../../components/shared/HotelsDropDown"
import { useLoader } from "../../hooks/useLoader"


export const ExternalEntityMapping = () => {
    const text = useTextResources()
    const loader = useLoader()
    const [data, setData] = useState([])
    const [deleted, setDeleted] = useState([])
    const [hotels, setHotels] = useState([])
    const [showOnlyUnMappedEntities, setShowOnlyUnMappedEntities] = useState(false)


    const entityTypeIds = [EntityType.Hotel, EntityType.Room, EntityType.AddOn, EntityType.RoomType,
    EntityType.Customer, EntityType.Wings, EntityType.Package, EntityType.HotelChain,
    EntityType.Rate, EntityType.ReservationPolicy, EntityType.Allocation, EntityType.PriceGroup,
    EntityType.StatisticSource, EntityType.ClassificationCategories, EntityType.Promotion];

    useEffect(() => {
        (async () => {
            const hotels = await Hotels.GetHotels()
            setHotels(hotels)
        })()
    }, [])

    const handleShowOnlyUnMappedEntities = useCallback(() => {
        setShowOnlyUnMappedEntities((prevState) => Boolean(!prevState), []);
    }, []);

    const entityTypeIdPredicate = (entityTypeId, filter) => {
        const entityTypeName = text(EntityTypes[entityTypeId]);
        return entityTypeName?.toLocaleLowerCase()?.includes(filter.value.toLocaleLowerCase()) === true;
    };

    const entityTypeFormatter = ({ value }) => {
        return text(EntityTypes[value]);
    }
    const onGridUpdate = (added, changed, deleted) => {
        if (deleted) {
            setDeleted(prev => [...prev, ...data.filter((x, i) => deleted.includes(i))])
        }
        const updated = onUpdateGridHandler(data, added, changed, deleted);
        setData(updated);
    }
    const hotelFormatter = ({ value }) => hotels.find(x => x.Id === value)?.Name || "Hotel Chain Level"

    const entityFormatter = ({ value, row }) => {
        switch (row.EntityTypeId) {
            case EntityType.AddOn:
                return <a target="_blank" rel="noopener noreferrer" href={`${endpoint}PMS/Addons/EditAddon?id=${value}`}>{value}</a>
            case EntityType.RoomType:
                return <a target="_blank" rel="noopener noreferrer" href={`${endpoint}PMS/RoomType/EditRoomType?roomID=${value}`}>{value}</a>
            case EntityType.Package:
                return <a target="_blank" rel="noopener noreferrer" href={`${endpoint}PMS/PackageInternal/Edit?packageId=${value}`}>{value}</a>
            case EntityType.Customer:
                return <a target="_blank" rel="noopener noreferrer" href={`${endpoint}CRM/Customers/Edit/${value}`}>{value}</a>
            case EntityType.Wings:
                return <a target="_blank" rel="noopener noreferrer" href={`${endpoint}Organization/HotelWings/HotelWings`}>{value}</a>
            case EntityType.Hotel:
                return <a target="_blank" rel="noopener noreferrer" href={`${endpoint}Organization/Hotel/EditHotel?id=${value}`}>{value}</a>
            case EntityType.Room:
                return <a target="_blank" rel="noopener noreferrer" href={`${endpoint}PMS/Rooms/EditRoom?roomID=${value}`}>{value}</a>
            case EntityType.HotelChain:
                return <a target="_blank" rel="noopener noreferrer" href={`${endpoint}Organization/HotelChain/Edit?Id=${value}`}>{value}</a>
            case EntityType.ReservationPolicy:
                return <a target="_blank" rel="noopener noreferrer" href={`${endpoint}PMS/ReservationPolicy/Index`}>{value}</a>
            case EntityType.Allocation:
                return <a target="_blank" rel="noopener noreferrer" href={`${endpoint}PMS/AllocationAndStrategy/Allocation`}>{value}</a>
            case EntityType.Rate:
                return <a target="_blank" rel="noopener noreferrer" href={`${endpoint}PMS/pricelist/Edit?RateId=${value}`}>{value}</a>
            case EntityType.Promotion:
                return <a target="_blank" rel="noopener noreferrer" href={`${endpoint}PMS/Promotion/Index`}>{value}</a>

            case EntityType.ClassificationCategories:
            case EntityType.StatisticSource:
            case EntityType.PriceGroup:
            default:
                return value;
        }
    }

    useEffect(() => {
        if (window.optima?.commands) {
            window.optima.commands.unsubscribe(MenuActions.save)
            window.optima.commands.subscribe(MenuActions.save, save);
        }
    }, [data, deleted])
    const save = () => {
        (async () => {
            try {
                loader(true)
                await ExtenalEntity.PostExtenalEntityMapping([...data, ...deleted.map(x => ({ ...x, Id: -x.Id }))])
                setDeleted([])
                onSuccess()
            } catch (error) {
                onError(error)
            } finally {
                loader(false)
            }
        })()
    }
    const gridOptions = {
        search: {
            fields: [
                <Form.Field
                    label={text('EntityType')}
                    placeholder={text('All')}
                    multiple
                    control={EntityTypesDropDown}
                    key="EntityTypeIds"
                    entityTypeIds={entityTypeIds}
                    clearable
                    width={4}
                />,
                <Form.Field
                    control={CheckBox}
                    key="ShowOnlyUnMappedEntities"
                    label={text("ShowUnmappedEntities")}
                    onChange={handleShowOnlyUnMappedEntities}
                    checked={Boolean(showOnlyUnMappedEntities)}
                />
            ],
            api: ExtenalEntity.GetAllExtenalEntitiesIncludeMappings,
            method: 'POST',
            searchOnLoad: true
        },
        grid: {
            columns: [
                { name: 'HotelId', title: text("Hotel"), formatter: hotelFormatter, editor: <HotelsDropDown clearable fluid placeholder='Hotel Chain Level' /> },
                { name: 'ExternalEntityId', title: text("ExternalPMSCode"), editor: <TextInput fluid placeHolder={text('ExternalCode')} /> },
                {
                    name: 'EntityTypeId',
                    title: text("EntityType"),
                    formatter: entityTypeFormatter,
                    editor: <EntityTypesDropDown fluid search entityTypeIds={entityTypeIds} />
                },
                { name: 'EntityId', title: text("CloudPMSId"), editor: <TextInput fluid placeHolder={text('EntityId')} />, formatter: entityFormatter }
            ],
            editable: {
                allowAdd: true,
                allowEdit: false,
                allowDelete: true
            },
            cacheId: 'ExternalEntityMapping',
            showFilter: false,
            filtering: {
                extensions: [
                    {
                        columnName: 'EntityTypeId',
                        predicate: entityTypeIdPredicate,
                    }
                ],
            },
            customOptions: {
                overrideData: true,
                group: {
                    grouping: [
                        { columnName: 'HotelId' }
                    ],
                    defaultExpandedGroups: hotels.map(x => x.Id.toString())
                }
            }
        }
    }

    return <GridSearchTemplate data={data} onSearchComplete={setData} options={gridOptions} onGridUpdate={onGridUpdate} />
}